// TokenAuthorization.js
import axios from "axios";

const inFlightRequests = new Map();
const recentResponseCache = new Map();
const RECENT_RESPONSE_TTL_MS = 1200;

const getStoredLogin = () => {
  try {
    return JSON.parse(localStorage.getItem("login")) || {};
  } catch {
    return {};
  }
};

const getAuthToken = () => {
  const storedAuth = getStoredLogin();
  return (
    storedAuth.token ||
    storedAuth.accessToken ||
    storedAuth.jwtToken ||
    storedAuth.authToken ||
    ""
  );
};

const getDefaultUserKeyID = () => {
  const storedLogin = getStoredLogin();
  return (
    storedLogin.userKeyID ||
    storedLogin.userKeyId ||
    storedLogin.UserKeyID ||
    null
  );
};

// Every list request is scoped to the logged-in user by default. Callers such
// as the dashboard can pass an employeeKeyID as userKeyID to override it.
const withDefaultListUserKeyID = (url, data) => {
  const isListEndpoint = /\/get[^/?]*(list|report)/i.test(String(url || ""));
  if (!isListEndpoint || !data || typeof data !== "object" || Array.isArray(data)) {
    return data;
  }

  return {
    ...data,
    userKeyID: data.userKeyID || getDefaultUserKeyID(),
  };
};

const buildRequestKey = (method, url, data, token = "") =>
  JSON.stringify({
    method,
    url,
    token,
    data: data ?? null,
  });

const isReusableListRequest = (method, url = "") => {
  const normalizedUrl = String(url || "").toLowerCase();

  if (method.startsWith("GET")) {
    return (
      normalizedUrl.includes("lookuplist") ||
      normalizedUrl.includes("get") ||
      normalizedUrl.includes("list")
    );
  }

  if (method.startsWith("POST")) {
    return (
      normalizedUrl.includes("get") &&
      (normalizedUrl.includes("list") || normalizedUrl.includes("lookup"))
    );
  }

  return false;
};

const getRecentCachedResponse = (requestKey) => {
  const cachedEntry = recentResponseCache.get(requestKey);

  if (!cachedEntry) {
    return null;
  }

  if (Date.now() - cachedEntry.timestamp > RECENT_RESPONSE_TTL_MS) {
    recentResponseCache.delete(requestKey);
    return null;
  }

  return cachedEntry.value;
};

const setRecentCachedResponse = (requestKey, response) => {
  recentResponseCache.set(requestKey, {
    value: response,
    timestamp: Date.now(),
  });
};

const getNestedTotalCount = (payload) => {
  const data = payload?.responseData?.data;
  const firstRow = Array.isArray(data) ? data[0] : null;
  const countCandidates = [
    payload?.totalCount,
    payload?.TotalCount,
    payload?.totalRecords,
    payload?.TotalRecords,
    payload?.recordCount,
    payload?.RecordCount,
    payload?.responseData?.totalCount,
    payload?.responseData?.TotalCount,
    payload?.responseData?.totalRecords,
    payload?.responseData?.TotalRecords,
    payload?.responseData?.recordCount,
    payload?.responseData?.RecordCount,
    data?.totalCount,
    data?.TotalCount,
    data?.totalRecords,
    data?.TotalRecords,
    data?.recordCount,
    data?.RecordCount,
    firstRow?.totalCount,
    firstRow?.TotalCount,
    firstRow?.totalRecords,
    firstRow?.TotalRecords,
    firstRow?.recordCount,
    firstRow?.RecordCount,
  ]
    .filter(
      (value) =>
        value !== null && value !== undefined && String(value).trim() !== "",
    )
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (countCandidates.length === 0) {
    return undefined;
  }

  return Math.max(...countCandidates);
};

const normalizeApiResponseMetadata = (payload, requestData = null) => {
  if (!payload || typeof payload !== "object") {
    return payload;
  }

  const responseRows = payload?.responseData?.data;
  const hasRowArray = Array.isArray(responseRows);
  const rows = hasRowArray ? responseRows : [];
  const requestedPageSize = Number(requestData?.pageSize);
  const requestedPageNo = Number(requestData?.pageNo || 0);
  const shouldApplyClientPaging =
    Number.isFinite(requestedPageSize) &&
    requestedPageSize > 0 &&
    rows.length > requestedPageSize;
  const nestedTotalCount = getNestedTotalCount(payload);
  const totalCount = hasRowArray
    ? Math.max(nestedTotalCount ?? 0, rows.length)
    : nestedTotalCount;
  const nextPayload =
    shouldApplyClientPaging && payload?.responseData
      ? {
          ...payload,
          responseData: {
            ...payload.responseData,
            data: rows.slice(
              Math.max(requestedPageNo, 0) * requestedPageSize,
              (Math.max(requestedPageNo, 0) + 1) * requestedPageSize,
            ),
          },
        }
      : payload;

  if (totalCount === undefined) {
    return nextPayload;
  }

  return {
    ...nextPayload,
    totalCount,
  };
};

const runWithInFlightDedup = (requestKey, requestFactory) => {
  if (inFlightRequests.has(requestKey)) {
    return inFlightRequests.get(requestKey);
  }

  const requestPromise = requestFactory().finally(() => {
    inFlightRequests.delete(requestKey);
  });

  inFlightRequests.set(requestKey, requestPromise);
  return requestPromise;
};

// Helper function to handle authorization errors
const handleAuthorizationError = (error) => {
  if (error.response && error.response.status === 401) {
    localStorage.clear();
    window.location.replace("/login");
  }
  return error; // Rethrow error for handling in slice
};

// POST API with Authorization
export const postApiWithAuthorization = async (url, data) => {
  const requestData = withDefaultListUserKeyID(url, data);
  const authToken = getAuthToken();
  const requestKey = buildRequestKey("POST_AUTH", url, requestData, authToken);
  const shouldReuseRecent = isReusableListRequest("POST_AUTH", url);
  const recentResponse = shouldReuseRecent
    ? getRecentCachedResponse(requestKey)
    : null;

  if (recentResponse) {
    return recentResponse;
  }

  try {
    const response = await runWithInFlightDedup(requestKey, () =>
      axios.post(url, requestData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }),
    );
    const normalizedResponse = normalizeApiResponseMetadata(response.data, requestData);
    if (shouldReuseRecent) {
      setRecentCachedResponse(requestKey, normalizedResponse);
    }
    return normalizedResponse; // Return response data directly
  } catch (error) {
    return handleAuthorizationError(error);
  }
};

// POST API without Authorization
export const postApiWithoutAuthorization = async (url, data) => {
  const requestKey = buildRequestKey("POST", url, data);
  const shouldReuseRecent = isReusableListRequest("POST", url);
  const recentResponse = shouldReuseRecent
    ? getRecentCachedResponse(requestKey)
    : null;

  if (recentResponse) {
    return recentResponse;
  }

  try {
    const response = await runWithInFlightDedup(requestKey, () =>
      axios.post(url, data),
    );
    const normalizedResponse = normalizeApiResponseMetadata(response.data, data);
    if (shouldReuseRecent) {
      setRecentCachedResponse(requestKey, normalizedResponse);
    }
    return normalizedResponse; // Return response data directly
  } catch (error) {
    return {
      statusCode: error?.response?.status || 500,
      responseData: error?.response?.data || null,
      message: error?.message || "Request failed",
    };
  }
};

// GET API with Authorization
export const getApiWithAuthorization = async (url) => {
  const authToken = getAuthToken();
  const requestKey = buildRequestKey("GET_AUTH", url, null, authToken);
  const shouldReuseRecent = isReusableListRequest("GET_AUTH", url);
  const recentResponse = shouldReuseRecent
    ? getRecentCachedResponse(requestKey)
    : null;

  if (recentResponse) {
    return recentResponse;
  }

  try {
    const response = await runWithInFlightDedup(requestKey, () =>
      axios.get(url, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }),
    );
    const normalizedResponse = normalizeApiResponseMetadata(response.data);
    if (shouldReuseRecent) {
      setRecentCachedResponse(requestKey, normalizedResponse);
    }
    return normalizedResponse; // Return response data directly
  } catch (error) {
    return handleAuthorizationError(error);
  }
};

// GET API without Authorization
export const getApiWithoutAuthorization = async (url) => {
  const requestKey = buildRequestKey("GET", url);
  const shouldReuseRecent = isReusableListRequest("GET", url);
  const recentResponse = shouldReuseRecent
    ? getRecentCachedResponse(requestKey)
    : null;

  if (recentResponse) {
    return recentResponse;
  }

  try {
    const response = await runWithInFlightDedup(requestKey, () =>
      axios.get(url),
    );
    const normalizedResponse = normalizeApiResponseMetadata(response.data);
    if (shouldReuseRecent) {
      setRecentCachedResponse(requestKey, normalizedResponse);
    }
    return normalizedResponse; // Return response data directly
  } catch (error) {
    return error; // Handle error in calling function
  }
};
